
import React, { useState, useEffect, useMemo } from "react";
import { Manifest, Block, BlockType, ConnectorDetails, PostDocument, PublicationType, Account } from "../../types";
import { TextBlockEditor, MediaBlockEditor, PollBlockEditor, FileBlockEditor } from "./BlockEditors";
import { getAllowedBlocksForType, getCompliantBlocks } from "../../utils/manifestUtils";
import { SocialPreview } from "./SocialPreview";
import { ManifestInfo } from "./ManifestInfo";
import { 
    Layout, AlertTriangle, Settings, Plus, Info, X,
    MessageSquare, Image as ImageIcon, BarChart2, ChevronRight, ChevronDown, ChevronUp, Eye, EyeOff, Layers, FileText, Check, Paperclip,
    Newspaper, FileImage, Film, Power, Users, Lock, Ban, User
} from "lucide-react";
import { stripMarkdown } from "../../utils/markdownUtils";

export interface ValidationIssue {
    network: string;
    message: string;
    type: 'error' | 'warning';
}

interface UniversalEditorProps {
    manifest: Manifest;
    connectors: ConnectorDetails[];
    document: PostDocument;
    onChange: (doc: PostDocument) => void;
    onValidationChange?: (issues: ValidationIssue[]) => void;
    accountsMap?: Record<string, Account[]>;
    selectedAccountIds?: Set<string>;
    onToggleAccount?: (accountId: string) => void;
    readOnly?: boolean;
    topContent?: React.ReactNode;
    bottomContent?: React.ReactNode;
}

export const UniversalEditor: React.FC<UniversalEditorProps> = ({ 
    manifest, 
    connectors, 
    document, 
    onChange, 
    onValidationChange,
    accountsMap = {},
    selectedAccountIds = new Set(),
    onToggleAccount,
    readOnly = false,
    topContent,
    bottomContent
}) => {
    const [activeTab, setActiveTab] = useState<string>("common");
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
    const [showMobilePreview, setShowMobilePreview] = useState(false);

    const activeManifest = useMemo(() => {
        if (activeTab === 'common') return manifest;
        return connectors.find(c => c.id === activeTab)?.manifest || manifest;
    }, [activeTab, manifest, connectors]);

    useEffect(() => {
        runValidation();
    }, [document.blocks, document.publicationType, connectors, accountsMap, selectedAccountIds]);

    const runValidation = () => {
        if (readOnly) return;
        const issues: ValidationIssue[] = [];
        
        // 1. Global Checks (checks the source document directly)
        document.blocks?.forEach(block => {
            if (block.type === 'media' && (!block.content.files || block.content.files.length === 0)) {
                // Only complain if this block is enabled for at least one connected network
                const isActiveSomewhere = connectors.some(c => !block.disabledNetworkIds?.includes(c.id));
                if (isActiveSomewhere) {
                    issues.push({ network: "Общее", message: "Медиа блок пуст. Добавьте файлы или удалите блок.", type: 'error' });
                }
            }
        });

        // 2. Per-Connector Checks (checks after filtering logic)
        connectors.forEach(conn => {
            const { validBlocks, ignoredBlocks, reason, warnings } = getCompliantBlocks(document.blocks || [], conn.manifest, document.publicationType || 'feed', conn.id);
            
            // Critical Errors: No valid blocks left for a network at all
            if (validBlocks.length === 0 && (document.blocks || []).some(b => !b.disabledNetworkIds?.includes(conn.id))) {
                 issues.push({ network: conn.displayName, message: "Контент полностью не подходит для этой сети.", type: 'error' });
            } 
            
            // Block-level Warnings (Entire blocks dropped)
            if (ignoredBlocks.length > 0) {
                issues.push({ network: conn.displayName, message: reason || "Часть блоков будет пропущена.", type: 'warning' });
            }

            // File-level Warnings (Specific files dropped)
            if (warnings && warnings.length > 0) {
                warnings.forEach(w => {
                    issues.push({ network: conn.displayName, message: w, type: 'warning' });
                });
            }

            // CRITICAL CHECK: Did filtering result in an empty required block?
            // Example: Media block had video, Instagram filtered it out -> Block is now empty -> ERROR
            validBlocks.forEach(vb => {
                if (vb.type === 'media' || vb.type === 'file') {
                    const compliantFiles = vb.content?.files || [];
                    
                    // Find original block to compare
                    const originalBlock = document.blocks?.find(b => b.id === vb.id);
                    const originalFiles = originalBlock?.content?.files || [];

                    // If original had files, but compliant has NONE, it's an error for this network
                    if (originalFiles.length > 0 && compliantFiles.length === 0) {
                        issues.push({ 
                            network: conn.displayName, 
                            message: "Все файлы исключены (формат не поддерживается). Блок не может быть пустым.", 
                            type: 'error' 
                        });
                    }
                }
            });
        });

        setValidationIssues(issues);
        if (onValidationChange) onValidationChange(issues);
    };

    const addBlock = (type: BlockType) => {
        if (readOnly || document.blocks?.some(b => b.type === type)) return;
        const newBlock: Block = {
            id: Math.random().toString(36).substr(2, 9),
            type,
            content: type === 'poll' ? { question: "", options: ["", ""] } : (type === 'media' || type === 'file') ? { files: [] } : { text: "" },
            disabledNetworkIds: []
        };
        onChange({ ...document, blocks: [...(document.blocks || []), newBlock] });
    };

    const updateBlock = (index: number, updated: Block) => {
        if (readOnly) return;
        const newBlocks = [...(document.blocks || [])];
        newBlocks[index] = updated;
        onChange({ ...document, blocks: newBlocks });
    };

    const removeBlock = (index: number) => {
        if (readOnly) return;
        onChange({ ...document, blocks: (document.blocks || []).filter((_, i) => i !== index) });
    };

    const updateModifier = (connectorId: string, modifierId: string, value: any) => {
        if (readOnly) return;
        const newSettings = { ...(document.networkSettings || {}) };
        newSettings[connectorId] = { ...(newSettings[connectorId] || {}), [modifierId]: value };
        onChange({ ...document, networkSettings: newSettings });
    };

    const activeNetworkPreview = activeTab === 'common' ? undefined : {
        id: activeTab,
        name: connectors.find(c => c.id === activeTab)?.displayName || "",
        manifest: connectors.find(c => c.id === activeTab)?.manifest || manifest
    };

    const getTypeDetails = (type: PublicationType | undefined) => {
        switch(type) {
            case 'story': return { label: 'История', icon: FileImage, color: 'text-pink-600', bg: 'bg-pink-50' };
            case 'reel': return { label: 'Reel', icon: Film, color: 'text-violet-600', bg: 'bg-violet-50' };
            default: return { label: 'Лента', icon: Newspaper, color: 'text-blue-600', bg: 'bg-blue-50' };
        }
    };

    const typeMeta = getTypeDetails(document.publicationType);

    // Get selected accounts for a specific connector
    const getSelectedAccountsForConnector = (connectorId: string) => {
        const accs = accountsMap[connectorId] || [];
        return accs.filter(a => selectedAccountIds.has(a.id));
    };
    
    // Get ALL selected accounts across all connectors
    const allSelectedAccounts = useMemo(() => {
        const list: {account: Account, connector: ConnectorDetails}[] = [];
        connectors.forEach(c => {
            const accs = accountsMap[c.id] || [];
            accs.filter(a => selectedAccountIds.has(a.id)).forEach(a => {
                list.push({ account: a, connector: c });
            });
        });
        return list;
    }, [connectors, accountsMap, selectedAccountIds]);

    return (
        <div className="flex flex-col lg:flex-row h-full bg-slate-50/50 font-sans overflow-hidden relative">
            
            {/* MOBILE PREVIEW FLOATING BUTTON (Visible only in readOnly or mobile mode) */}
            <button 
                onClick={() => setShowMobilePreview(true)}
                className="lg:hidden fixed bottom-6 right-6 z-[60] w-14 h-14 bg-blue-600 text-white rounded-full shadow-2xl flex items-center justify-center animate-bounce hover:animate-none active:scale-90 transition-all border-4 border-white"
            >
                <Eye className="w-6 h-6" />
            </button>

            {/* Sidebar: Combined Navigation and Channels */}
            <div className="w-full lg:w-72 bg-white lg:border-r border-b lg:border-b-0 border-slate-200 flex flex-row lg:flex-col flex-shrink-0 z-20 overflow-x-auto lg:overflow-y-auto lg:overflow-visible shadow-sm lg:shadow-none custom-scrollbar">
                <div className="hidden lg:block p-6 border-b border-slate-100">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Навигация</h3>
                    <div className={`flex items-center gap-4 p-4 rounded-2xl border border-slate-100 ${typeMeta.bg}`}>
                        <div className={`p-2.5 bg-white rounded-xl shadow-sm ${typeMeta.color}`}><typeMeta.icon className="w-6 h-6" /></div>
                        <div>
                            <div className="text-[9px] uppercase font-black text-slate-400 tracking-widest leading-none mb-1">Режим</div>
                            <div className={`text-sm font-black ${typeMeta.color}`}>{typeMeta.label}</div>
                        </div>
                    </div>
                </div>
                
                <div className="p-2 lg:p-4 flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch h-full">
                    <button onClick={() => setActiveTab("common")} className={`flex-shrink-0 lg:w-full flex items-center px-5 py-3 rounded-2xl text-xs sm:text-sm font-black transition-all whitespace-nowrap ${activeTab === 'common' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-500 hover:bg-slate-50'}`}>
                        <Layout className={`w-5 h-5 mr-3 sm:mr-4 flex-shrink-0 ${activeTab === 'common' ? 'text-white' : 'text-slate-400'}`} />
                        <span>Общее</span>
                    </button>
                    
                    <div className="hidden lg:block pt-4 pb-1 px-3"><h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Социальные сети</h3></div>
                    
                    {connectors.map(c => {
                        const selectedForThis = getSelectedAccountsForConnector(c.id);
                        if (selectedForThis.length === 0 && !readOnly) return null; // Hide if no accounts selected in wizard (optional, but cleaner)

                        return (
                            <div key={c.id} className="flex flex-col gap-1 w-full flex-shrink-0 lg:flex-shrink">
                                <button onClick={() => setActiveTab(c.id)} className={`flex-shrink-0 w-full flex items-center px-5 py-3 rounded-2xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${activeTab === c.id ? 'bg-slate-100 text-slate-900 border border-slate-200' : 'text-slate-500 hover:bg-slate-50 border border-transparent'}`}>
                                    <div className="w-6 h-6 mr-3 sm:mr-4 bg-slate-50 rounded-lg p-1 border border-slate-100 flex-shrink-0">
                                        <img src={c.logoUrl} className="w-full h-full object-contain" alt="" />
                                    </div>
                                    <span className="truncate">{c.displayName}</span>
                                </button>
                                
                                {/* Selected Accounts List under the connector */}
                                <div className="hidden lg:flex flex-col gap-1 pl-14 pr-2">
                                    {selectedForThis.map(acc => (
                                        <div key={acc.id} className="flex items-center gap-2 text-[10px] text-slate-400 font-medium py-0.5 animate-in slide-in-from-left-2 fade-in duration-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></div>
                                            <span className="truncate">{acc.credentials.displayName || acc.credentials.meta?.botUsername || "Account"}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto relative bg-slate-50/30 custom-scrollbar">
                <div className="max-w-3xl mx-auto p-4 sm:p-6 md:p-10 min-h-full pb-32">
                    {/* Injected Top Metadata */}
                    {topContent}

                    {activeTab === 'common' ? (
                        <div className="space-y-6">
                            {/* Recipients Summary Block */}
                            {!readOnly && allSelectedAccounts.length > 0 && (
                                <div className="bg-white rounded-[1.5rem] border border-slate-200 p-5 shadow-sm animate-in fade-in slide-in-from-top-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                            <Users className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs font-black text-slate-800 uppercase tracking-wide">Получатели</div>
                                            <div className="text-[10px] font-bold text-slate-400">{allSelectedAccounts.length} аккаунтов выбрано</div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {allSelectedAccounts.map(({account, connector}) => (
                                            <div key={account.id} className="flex items-center gap-2 pl-1 pr-3 py-1 bg-slate-50 border border-slate-100 rounded-full">
                                                <div className="relative">
                                                    <div className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[9px] font-black text-slate-500 overflow-hidden">
                                                        {(account.credentials.displayName?.[0] || account.credentials.meta?.botUsername?.[0] || "A").toUpperCase()}
                                                    </div>
                                                    <img src={connector.logoUrl} className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border border-white" alt="" />
                                                </div>
                                                <span className="text-[10px] font-bold text-slate-600 truncate max-w-[100px]">
                                                    {account.credentials.displayName || account.credentials.meta?.botUsername || "Account"}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!readOnly && (
                                <div className="mb-2">
                                    <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Контент публикации</h2>
                                    <p className="text-slate-500 text-xs sm:text-sm font-medium">Создайте универсальный пост для всех каналов</p>
                                </div>
                            )}

                            <div className="space-y-5">
                                {(document.blocks || []).map((block, idx) => (
                                    <div key={block.id} className="relative animate-in slide-in-from-bottom-4 duration-500">
                                        {block.type === 'text' && <TextBlockEditor readOnly={readOnly} block={block} manifest={activeManifest} onChange={(b) => updateBlock(idx, b)} onRemove={() => removeBlock(idx)} publicationType={document.publicationType || 'feed'} />}
                                        {block.type === 'media' && <MediaBlockEditor readOnly={readOnly} block={block} manifest={activeManifest} onChange={(b) => updateBlock(idx, b)} onRemove={() => removeBlock(idx)} publicationType={document.publicationType || 'feed'} />}
                                        {block.type === 'file' && <FileBlockEditor readOnly={readOnly} block={block} manifest={activeManifest} onChange={(b) => updateBlock(idx, b)} onRemove={() => removeBlock(idx)} publicationType={document.publicationType || 'feed'} />}
                                        {block.type === 'poll' && <PollBlockEditor readOnly={readOnly} block={block} manifest={activeManifest} onChange={(b) => updateBlock(idx, b)} onRemove={() => removeBlock(idx)} publicationType={document.publicationType || 'feed'} />}
                                    </div>
                                ))}
                            </div>

                            {/* Block Selection Grid */}
                            {!readOnly && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 pt-6 border-t border-slate-200">
                                    {[
                                        { type: 'text', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Текст' },
                                        { type: 'media', icon: ImageIcon, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Медиа' },
                                        { type: 'poll', icon: BarChart2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Опрос' },
                                        { type: 'file', icon: Paperclip, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Файл' }
                                    ].map(b => {
                                        // 1. Check if block is already added (blocks can only be added once)
                                        const isAdded = (document.blocks || []).some(existing => existing.type === b.type);
                                        
                                        // 2. Check if block is supported by AT LEAST ONE selected connector
                                        const isSupported = connectors.some(c => c.manifest.supports.blocks.includes(b.type as BlockType));
                                        
                                        const isDisabled = isAdded || !isSupported;

                                        return (
                                            <button 
                                                key={b.type} 
                                                onClick={() => !isDisabled && addBlock(b.type as BlockType)} 
                                                disabled={isDisabled}
                                                className={`
                                                    relative flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-5 rounded-3xl border transition-all group
                                                    ${isDisabled 
                                                        ? 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed' 
                                                        : 'bg-white border-slate-200 hover:border-blue-500 active:scale-95 cursor-pointer'
                                                    }
                                                `}
                                            >
                                                <div className={`
                                                    p-3 sm:p-4 rounded-2xl transition-all
                                                    ${isDisabled ? 'bg-slate-100 text-slate-400' : `${b.bg} ${b.color} group-hover:bg-blue-600 group-hover:text-white`}
                                                `}>
                                                    {isDisabled ? (isAdded ? <Check className="w-5 h-5 sm:w-6 sm:h-6" /> : <Ban className="w-5 h-5 sm:w-6 sm:h-6" />) : <b.icon className="w-5 h-5 sm:w-6 sm:h-6" />}
                                                </div>
                                                <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}>
                                                    {b.label}
                                                </span>
                                                
                                                {isDisabled && (
                                                    <div className="absolute top-2 right-2">
                                                        {isAdded && <span className="text-[8px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Добавлено</span>}
                                                        {!isSupported && !isAdded && <span className="text-[8px] font-bold text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded">Нельзя</span>}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Injected Bottom Metadata */}
                            {bottomContent}
                        </div>
                    ) : (
                        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-300">
                            <div className="flex items-center gap-4 sm:gap-6 bg-white p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-slate-100">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-contain bg-slate-50 p-2 border border-slate-100 flex items-center justify-center">
                                    <img src={connectors.find(c => c.id === activeTab)?.logoUrl} className="max-w-full max-h-full" alt=""/>
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">{connectors.find(c => c.id === activeTab)?.displayName}</h2>
                                    <p className="text-slate-400 text-[9px] sm:text-[10px] uppercase font-black tracking-widest mt-1">Параметры канала</p>
                                </div>
                            </div>
                            
                            <ManifestInfo manifest={activeManifest} publicationType={document.publicationType || 'feed'} />
                            
                            {activeManifest.modifiers && activeManifest.modifiers.length > 0 && (
                                <div className="bg-white p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-200 shadow-sm space-y-4 sm:space-y-6">
                                    <h4 className="font-black text-slate-800 flex items-center gap-3 text-xs sm:text-sm uppercase tracking-widest"><Settings className="w-5 h-5 text-blue-500" /> Настройка публикации</h4>
                                    <div className="grid gap-2 sm:gap-3">
                                        {activeManifest.modifiers.map(m => (
                                            <label key={m.id} className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-2xl border border-slate-100 cursor-pointer hover:bg-blue-50 transition-colors group">
                                                <span className="text-xs sm:text-sm font-bold text-slate-700">{m.label}</span>
                                                <input 
                                                    type="checkbox" 
                                                    disabled={readOnly} 
                                                    // FIX: Defensive check for networkSettings
                                                    checked={document.networkSettings?.[activeTab]?.[m.id] || false} 
                                                    onChange={(e) => updateModifier(activeTab, m.id, e.target.checked)} 
                                                    className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 rounded-lg border-2 border-slate-300 focus:ring-blue-500 transition-all cursor-pointer" 
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Sidebar: Social Preview (Adaptive Drawer on Mobile) */}
            <div className={`fixed inset-0 z-[80] bg-slate-50 lg:static lg:z-auto lg:block lg:w-96 lg:border-l border-slate-200 flex flex-col transition-transform duration-300 ${showMobilePreview ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}>
                <div className="lg:hidden bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center shadow-sm">
                    <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-blue-600" />
                        <h3 className="font-black uppercase text-xs tracking-widest">Предпросмотр</h3>
                    </div>
                    <button onClick={() => setShowMobilePreview(false)} className="p-2 bg-slate-100 rounded-xl text-slate-500 active:scale-90"><X /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar bg-slate-50/50">
                    <h3 className="hidden lg:flex text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8 items-center gap-3"><Eye className="w-4 h-4" /> Вид публикации</h3>
                    <div className="sticky top-0">
                        <SocialPreview 
                            blocks={(document.blocks || []).filter(b => {
                                if (activeTab === 'common') return !b.disabledNetworkIds?.includes('');
                                const connector = connectors.find(c => c.id === activeTab);
                                if (!connector) return true;
                                const { validBlocks } = getCompliantBlocks((document.blocks || []).filter(block => !block.disabledNetworkIds?.includes(activeTab)), connector.manifest, document.publicationType || 'feed');
                                return validBlocks.includes(b);
                            })} 
                            activeNetwork={activeNetworkPreview} 
                            publicationType={document.publicationType || 'feed'}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
